<template>
  <div
    class="image-container"
    :style="{ padding: isMiniMap ? '0' : '50px 0' }"
    :id="elementUUID"
    @wheel="onWheel"
    @mousedown="startPan"
    @mousemove="onPan"
    @mouseup="endPan"
    @mouseleave="endPan"
    @touchstart="startPan"
    @touchmove="onPan"
    @touchend="endPan"
  >
    <!-- Drag Hint -->
    <transition name="fade-slide">
      <div v-if="!isDragging" class="drag-hint">
        <v-icon class="hint-icon">mdi-hand-back-right-outline</v-icon>
        <span class="hint-text">Drag to move</span>
      </div>
    </transition>

    <!-- Zoomable Content -->
    <div
      class="zoomable-content"
      :id="zoomableContentUUID"
      :style="{
        width: !isMiniMap ? `${zoomableContentWith}%` : '100%',
        height: !isMiniMap ? 'auto' : '100%',
        ...imageStyle,
      }"
    >
      <!-- Base Image Slot -->
      <slot name="Image"></slot>
      <!-- Hotspot Slot Positioned Above the Image -->
      <slot name="Hotspot"></slot>
    </div>

    <!-- Buttons for Zoom Controls -->
    <div class="controls" v-if="!isMiniMap" ref="controlsRef">
      <v-btn :disabled="scale === 5" @click="zoomIn">
        <v-icon color="primary">mdi-plus-thick</v-icon>
      </v-btn>
      <v-btn :disabled="scale === calculateInitialScale()" @click="zoomOut">
        <v-icon color="primary">mdi-minus-thick</v-icon>
      </v-btn>
      <v-btn :disabled="!displayResetButton" @click="resetPosition">
        <v-icon color="primary">mdi-magnify-remove-cursor</v-icon>
      </v-btn>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useDisplay } from 'vuetify';
import { generateUUID } from '@/Core.Patterns/Factory';

const elementUUID = generateUUID();
const zoomableContentUUID = generateUUID();

const { name } = useDisplay();

const zoomableContentWith = computed(() => {
  if (name.value === 'lg') return 60;
  if (name.value === 'xl') return 70;
  if (name.value === 'xxl') return 75;
  return 100;
});

const props = defineProps<{
  isMiniMap?: boolean;
  isDialog?: boolean;
}>();

const imageContainerWidth = ref(0);
const imageWidth = ref(0);
const imageContainer = ref<HTMLElement | null>(null);
const imageElement = ref<HTMLElement | null>(null);

const calculateYOffset = () => {
  if (imageContainer.value) {
    const containerHeight = imageContainer.value.clientHeight;
    return containerHeight > 1080 ? 500 : 800;
  }
  return 800;
};

const calculateInitialScale = () => {
  if (imageContainer.value) {
    const containerHeight = imageContainer.value.clientHeight;
    return containerHeight > 1080 ? 0.7 : 0.5;
  }
  return 0.5;
};

let observer: ResizeObserver | null = null;
onMounted(() => {
  imageContainer.value = document.getElementById(elementUUID);
  imageElement.value = document.getElementById(zoomableContentUUID);

  if (
    imageContainer.value &&
    imageElement.value &&
    !props.isMiniMap &&
    !props.isDialog
  ) {
    // Set initial scale based on container height
    scale.value = calculateInitialScale();

    observer = new ResizeObserver(() => {
      const containerW = imageContainer.value!.clientWidth;
      const elementH = imageElement.value?.clientHeight ?? 0;

      // Skip until both the container and the image have real dimensions
      if (containerW === 0 || elementH === 0) return;

      imageContainerWidth.value = containerW;
      imageWidth.value = imageElement.value!.clientWidth;
      position.value.x = (imageContainerWidth.value - imageWidth.value) / 2;
      position.value.y =
        ((imageContainer.value?.clientHeight ?? 0) -
          calculateYOffset() -
          elementH) /
        2;

      // Recalculate scale when container resizes
      scale.value = calculateInitialScale();

      // Reveal the map only after the first valid position is set
      isReady.value = true;
    });

    observer.observe(imageContainer.value);
    observer.observe(imageElement.value);
  }
});

onBeforeUnmount(() => {
  if (observer) observer.disconnect();
});

type Position = { x: number; y: number };

const isReady = ref(false);

const scale = ref(0.5); // Will be updated in onMounted
const position = ref<Position>({ x: 0, y: 0 });

const panStart = ref<Position | null>(null);

const imageStyle = computed(() => ({
  transform: `scale(${scale.value}) translate(${position.value.x}px, ${position.value.y}px)`,
  transformOrigin: 'center center',
  // No transition on the first paint — snap directly to the correct position,
  // then enable smooth transitions for all subsequent zoom/pan interactions.
  transition: isReady.value ? 'transform 0.2s ease, opacity 0.3s ease' : 'none',
  opacity: isReady.value || props.isMiniMap ? 1 : 0,
}));

const initialScale = computed(() => calculateInitialScale());

const displayResetButton = computed(
  () =>
    scale.value !== initialScale.value ||
    position.value.x !== (imageContainerWidth.value - imageWidth.value) / 2 ||
    position.value.y !==
      ((imageContainer.value?.clientHeight ?? 0) -
        calculateYOffset() -
        (imageElement.value?.clientHeight ?? 0)) /
        2
);

const onWheel = (event: WheelEvent) => {
  event.preventDefault();
  const zoomIntensity = 0.1;
  const direction = event.deltaY > 0 ? -1 : 1;
  const minScale = calculateInitialScale();
  scale.value = Math.min(
    Math.max(scale.value + direction * zoomIntensity, minScale),
    5
  );
};

const isDragging = ref(false);
const controlsRef = ref<HTMLElement | null>(null);

const startPan = (event: MouseEvent | TouchEvent) => {
  const target = event.target as HTMLElement;

  if (controlsRef.value?.contains(target)) {
    return; // Don't trigger dragging when clicking on controls
  }

  event.preventDefault();
  isDragging.value = true;

  const clientX = 'touches' in event ? event.touches[0].clientX : event.clientX;
  const clientY = 'touches' in event ? event.touches[0].clientY : event.clientY;

  panStart.value = {
    x: clientX - position.value.x,
    y: clientY - position.value.y,
  };
};

const onPan = (event: MouseEvent | TouchEvent) => {
  if (!panStart.value) return;

  const clientX = 'touches' in event ? event.touches[0].clientX : event.clientX;
  const clientY = 'touches' in event ? event.touches[0].clientY : event.clientY;

  position.value.x = clientX - panStart.value.x;
  position.value.y = clientY - panStart.value.y;
};

const endPan = () => {
  panStart.value = null;
  isDragging.value = false;
};

const zoomIn = () => {
  scale.value = Math.min(scale.value + 0.2, 5); // Limit max zoom to 5x
};

const zoomOut = () => {
  const minScale = calculateInitialScale();
  scale.value = Math.max(scale.value - 0.2, minScale);
};

const resetPosition = () => {
  scale.value = calculateInitialScale();
  position.value = {
    x: (imageContainerWidth.value - imageWidth.value) / 2,
    y:
      ((imageContainer.value?.clientHeight ?? 0) -
        calculateYOffset() -
        (imageElement.value?.clientHeight ?? 0)) /
      2,
  };
};
</script>

<style lang="scss" scoped>
.image-container {
  width: 100%;
  height: 100%;
  position: relative;
  cursor: grab;
}

.loader {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.image-container:active {
  cursor: grabbing;
}

.zoomable-content {
  position: relative;
}

.zoomable-content slot[name='Image'] {
  position: relative;
}

.hotspot-layer slot[name='Hotspot'] {
  pointer-events: auto;
}

/* Buttons for zoom controls */
.controls {
  position: absolute;
  top: 10px;
  right: 10px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.controls button {
  background-color: #fff;
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 5px 10px;
  cursor: pointer;
  font-size: 16px;
  text-transform: none;
  color: #2e3c54;
}

.controls button:hover {
  background-color: #f0f0f0;
}

.drag-hint {
  position: absolute;
  top: 10px;
  left: 10px;
  display: flex;
  align-items: center;
  background-color: rgba(255, 255, 255, 0.9);
  border: 1px solid #ccc;
  border-radius: 6px;
  padding: 6px 10px;
  z-index: 10;

  .hint-icon {
    margin-right: 6px;
    color: #2e3c54;
  }

  .hint-text {
    font-size: 14px;
    color: #2e3c54;
    white-space: nowrap;
  }
}

.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.3s ease;
}

.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-5px);
}

.fade-slide-enter-to,
.fade-slide-leave-from {
  opacity: 1;
  transform: translateY(0);
}
</style>
